import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { ChangeLogFilters } from "@/components/admin/ChangeLogFilters"
import { ChangeLogFields } from "@/components/admin/ChangeLogFields"
import { ChangeLogSubNav } from "@/components/admin/ChangeLogSubNav"
import { Pagination } from "@/components/Pagination"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type { Pagination as PaginationMeta } from "@/types/pagination"
import type {
  ChangeLogFilterOptions,
  ChangeLogFilterValues,
  ChangeLogRow,
} from "@/types/change_log"

type Props = {
  entries: ChangeLogRow[]
  filters: ChangeLogFilterValues
  filter_options: ChangeLogFilterOptions
  pagination: PaginationMeta
}

const DESCRIPTION =
  "Who changed what, when, and from where — across sign-ins, automations, BUMP Training, users, access, delivery settings and Weekly Reports."

export default function ChangeLogIndex({
  entries,
  filters,
  filter_options,
  pagination,
}: Props) {
  return (
    <>
      <Head title="Change log">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Change log" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Change log"
          description="Every recorded change keeps the old value as well as the new one, so it answers what a setting used to say — not only that somebody edited it."
          tabs={<ChangeLogSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <ChangeLogFilters filters={filters} options={filter_options} />

          {pagination.total_count === 0 && !isFiltered(filters) ? (
            <div className="callout">
              <p className="m-0">
                Nothing has been recorded yet. From now on, every sign-in and sign-out
                and every change to an automation, a BUMP Training entry, a user's admin
                access, an access group or override, the delivery settings, or who
                appears on the Weekly Reports grid is written here.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>When</TableHeaderCell>
                    <TableHeaderCell>Module</TableHeaderCell>
                    <TableHeaderCell>Record</TableHeaderCell>
                    <TableHeaderCell>What changed</TableHeaderCell>
                    <TableHeaderCell>Who</TableHeaderCell>
                    <TableHeaderCell>Where from</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.length === 0 && (
                    <TableEmpty colSpan={6}>No changes match these filters.</TableEmpty>
                  )}

                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell nowrap>
                        <time dateTime={entry.occurred_at}>{entry.occurred_at_label}</time>
                      </TableCell>

                      <TableCell nowrap>
                        <Badge tone={entry.module_tone}>{entry.module_label}</Badge>
                      </TableCell>

                      <TableCell>
                        <span className="block max-w-56 truncate" title={entry.record_label ?? undefined}>
                          {entry.record_label ?? "—"}
                        </span>
                        {entry.record_type_label && (
                          <span className="block text-xs text-ink-muted">
                            {entry.record_type_label}
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <ChangeLogFields entry={entry} />
                      </TableCell>

                      <TableCell nowrap>
                        {entry.user_id ? (
                          <Link href={`/admin/users/${entry.user_id}`}>{entry.actor_name}</Link>
                        ) : (
                          entry.actor_name
                        )}
                      </TableCell>

                      <TableCell nowrap>
                        <span className="block">{entry.ip_address ?? "—"}</span>
                        {entry.browser_label && (
                          <span
                            className="block text-xs text-ink-muted"
                            title={entry.user_agent ?? undefined}
                          >
                            {entry.browser_label}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                meta={pagination}
                path="/admin/change_log"
                params={{
                  module: filters.module === "all" ? null : filters.module,
                  kind: filters.kind === "all" ? null : filters.kind,
                  user_id: filters.user_id,
                  from: filters.from,
                  to: filters.to,
                }}
                label="entries"
              />
            </>
          )}
        </div>
      </AdminShell>
    </>
  )
}

function isFiltered(filters: ChangeLogFilterValues) {
  return (
    filters.module !== "all" ||
    filters.kind !== "all" ||
    filters.user_id !== null ||
    Boolean(filters.from) ||
    Boolean(filters.to)
  )
}
